import { KendraConst, SearchInterface } from "@amzn/ask-legal-domain";
import { Box, SpaceBetween } from "@amzn/awsui-components-react";
import { QueryResultItem }  from "@aws-sdk/client-kendra";
import * as React from "react";
import { AppContext } from "../../setup/context";
import { useAPI2 } from "../../hooks/api-hook";
import { useInstanceDataContext } from "../../contexts/InstanceDataContext";
import { useCrossInstanceSearch } from "../../hooks/use-cross-instance-search";
import { UIModel } from "../../model/ui-model";
import { getInstanceIdFromResult } from "../../utils/search-utils";
import { SmartAnswersView } from "./search-components/SmartAnswersView";
import { SearchResultItem } from "./search-components/SearchResultItem";
import "../../styles/component/layout/SearchResultPageLayout.scss";

export const SearchResultPanel = (props: {
    instanceId?: string;
    results: SearchInterface.SearchQueryResponse;
    queryInputState: UIModel.State<SearchInterface.SearchQueryInput>;
}) => {
    const context = React.useContext(AppContext);
    const submitFeedbackRunner = useAPI2(
        context.getSearchAPI().feedback
    );
    const isCrossInstanceSearchEnabled = useCrossInstanceSearch();
    const { loadInstance, instanceCache } = useInstanceDataContext();
    const suggestedResults: QueryResultItem[] = [];
    const faqResults: QueryResultItem[] = [];
    const linkResults: QueryResultItem[] = [];
    const otherResults: QueryResultItem[] = [];

    const submitClickFeedback = (resultId: string) => {
        submitFeedbackRunner.invoke(
            SearchInterface.SubmitFeedbackInput.create({
                instanceId: props.instanceId!,
                queryId: props.results.result.QueryId!,
                clickFeedbackItems: [{
                    ClickTime: new Date(),
                    ResultId: resultId
                }]
            })
        );
    };

    React.useEffect(() => {
        if (isCrossInstanceSearchEnabled || !props.instanceId) {
            const instanceIds = props.results.result.ResultItems
                .map(getInstanceIdFromResult)
                .filter(Boolean) as string[];
            const uniqueInstanceIds = [...new Set(instanceIds)];
            const missingIds = uniqueInstanceIds.filter(id => !instanceCache[id]);
            missingIds.forEach(instanceId => {
                loadInstance(instanceId).catch(console.error);
            });
        }
    }, [props.results, isCrossInstanceSearchEnabled, props.instanceId, loadInstance, instanceCache]);

    props.results.result.ResultItems.forEach(r => {
        if (r.Type === "ANSWER") {
            suggestedResults.push(r);
        } else if (r.Type === "QUESTION_ANSWER") {
            if (r.DocumentAttributes.find(a => a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.ContentType).Value.StringValue === "Link") {
                linkResults.push(r);
            } else {
                faqResults.push(r);
            }
        } else {
            otherResults.push(r);
        }
    });

    return (
        <SpaceBetween direction="vertical" size="l">
            { props.results.result.TotalNumberOfResults === 0 && (
                <div>No relevant resources found</div>
            )}
            {/* Show Smart Answers View when instanceId is provided (instance-specific search) */}
            { props.instanceId && (suggestedResults.length > 0 || faqResults.length > 0 || linkResults.length > 0) && (
                <SmartAnswersView
                    instanceId={props.instanceId}
                    searchQueryId={props.results.result.QueryId!}
                    suggestedResults={suggestedResults}
                    faqResults={faqResults}
                    linkResults={linkResults}
                />
            )}
            { otherResults.length > 0 && (
                <div>
                    <Box variant="small">
                        Displaying {otherResults.length} of {
                        (props.results.result.TotalNumberOfResults - suggestedResults.length - faqResults.length) > 100 ?
                        100 : (props.results.result.TotalNumberOfResults - suggestedResults.length - faqResults.length)
                    } results
                    </Box>
                    <br/>
                    <br/>
                    <SpaceBetween direction="vertical" size="l">
                        {otherResults.map((r: QueryResultItem) => (
                            <SearchResultItem
                                key={r.Id}
                                item={r}
                                currentInstanceId={props.instanceId}
                                submitClickFeedback={submitClickFeedback}
                            />
                        ))}
                    </SpaceBetween>
                </div>
            )}
        </SpaceBetween>
    );
};
